import axiosNest from '../../api/axiosNest';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { toApiInput, type PreOrderInput } from '../../pre-order/preOrderApi';

/**
 * Zapytania klienta, dostępność aut i wycena — klient API
 * (`apps/api/src/inquiries/`, 26.10.2026, P3; przełączniki
 * `clientInquiries` S-26.10-08, `inquiryQuotes` S-26.10-09,
 * `inquiryOutcomes` S-26.10-10). Z systemu nic nie jest wysyłane — szkice
 * wiadomości kopiuje albo otwiera w poczcie człowiek i oznacza „wysłane ręcznie”.
 */
export const FEATURE_INQUIRIES = 'clientInquiries';
export const FEATURE_INQUIRY_QUOTES = 'inquiryQuotes';
export const FEATURE_INQUIRY_OUTCOMES = 'inquiryOutcomes';

/** Stany zapytania — kody z API (dane), napisy jak w API. */
export const INQUIRY_STATUSES: ReadonlyArray<{ id: string; name: string }> = [
  { id: 'to_check', name: 'do sprawdzenia' },
  { id: 'priced', name: 'wycenione' },
  { id: 'offer_sent', name: 'oferta wysłana' },
  { id: 'accepted', name: 'przyjęte' },
  { id: 'rejected', name: 'odrzucone' },
  { id: 'no_response', name: 'brak odpowiedzi' },
  { id: 'declined', name: 'odmowa' },
];

/** Podpowiedzi powodów z makiety — zapisujemy tekst człowieka. */
export const REJECTION_REASON_HINTS = ['cena', 'brak auta w terminie', 'klient wybrał innego przewoźnika', 'nieznany'];
export const DECLINE_REASON_HINTS = ['brak auta w terminie', 'relacja poza obsługą'];
export const MIN_REASON_LENGTH = 3;
export const MIN_PRICE_CHANGE_REASON = 10;

export interface InquiryRow {
  id: number;
  ref: string;
  kind: string | null;
  clientName: string | null;
  loading: string | null;
  unloading: string | null;
  loadingDate: string | null;
  unloadingDate: string | null;
  status: string;
  statusLabel: string;
  statusReason: string | null;
  indicatedCarName: string | null;
  forwardedToFtlAt: string | null;
  ftlResponse: string | null;
  tradeSignalAt: string | null;
  quote: { price: number | null; currency: string; approved: boolean; offerSent: boolean; superseded: boolean } | null;
  createdAt: string;
}

export interface InquiryFilter {
  status?: string;
  open?: string;
  forwarded?: string;
  tradeSignal?: string;
  awaitingApproval?: string;
  q?: string;
}

export interface InquiryQuote {
  id: number;
  version: number;
  price: number | null;
  currency: string;
  basis: QuoteBasis | null;
  proposedBy: string | null;
  proposedAt: string;
  proposalNote: string | null;
  approvedPrice: number | null;
  approvedBy: string | null;
  approvedRoleName: string | null;
  approvedAt: string | null;
  approvalNote: string | null;
  offerSentAt: string | null;
  offerSentBy: string | null;
  offerSubject: string | null;
  result: string | null;
  resultLabel: string | null;
  resultReason: string | null;
  resultAt: string | null;
  superseded: boolean;
}

export interface InquiryActions {
  edit: boolean;
  indicate: boolean;
  forwardToFtl: boolean;
  ftlRespond: boolean;
  tradeSignal: boolean;
  decline: boolean;
  confirmationSent: boolean;
  propose: boolean;
  approve: boolean;
  offerSent: boolean;
  outcome: boolean;
}

export interface InquiryDetail {
  id: number;
  ref: string;
  kind: string | null;
  isInquiry: boolean;
  userOfferId: number | null;
  header: {
    companyId: number | null;
    companyName: string | null;
    contractorId: number | null;
    contractorName: string | null;
    clientName: string | null;
    loadingCity: string | null;
    loadingCountry: string | null;
    loadingPostalCode: string | null;
    unloadingCity: string | null;
    unloadingCountry: string | null;
    unloadingPostalCode: string | null;
    loadingDate: string | null;
    unloadingDate: string | null;
    cargo: string | null;
    weight: number | null;
    pallets: number | null;
    requiredVehicleType: string | null;
    createdBy: string | null;
    createdAt: string;
  };
  inquiry: {
    status: string;
    statusLabel: string;
    statusSince: string;
    statusBy: string | null;
    statusReason: string | null;
    sourceNote: string | null;
    points: { from: unknown | null; to: unknown | null; note: string | null };
    indicated: { carId: number; carName: string | null; by: string | null; at: string; note: string | null } | null;
    forwardedToFtl: {
      at: string;
      by: string | null;
      note: string | null;
      response: string | null;
      responseLabel: string | null;
      responseNote: string | null;
      responseBy: string | null;
      responseAt: string | null;
    } | null;
    tradeSignal: { at: string; by: string | null; note: string | null } | null;
    confirmation: { at: string; by: string | null; subject: string | null } | null;
  };
  quotes: InquiryQuote[];
  features: { quotes: boolean; outcomes: boolean };
  actions: InquiryActions;
}

export interface QuoteBasis {
  computedAt: string;
  pricingRule: string;
  route: {
    source: string;
    km: number | null;
    driveHours: number | null;
    estimateKm: number | null;
    tolls: { pln: number | null; amount: number | null; currency: string | null; note: string | null } | null;
    note: string | null;
  } | null;
  routeNote: string | null;
  priceStatistics: { text: string; basis: string } | null;
  range: { fromEur: number; toEur: number; medianEur: number; basis: string } | null;
  ownCost: { pln: number | null; carId: number; carName: string | null; accessKm: number; note: string } | null;
  fuelSurcharges: string[];
  price: { amount: number; currency: string; valuePln: number | null; valueNote: string | null } | null;
  margin: { pln: number | null; pct: number | null; missing: string[]; band: string | null; bandRule: string | null } | null;
  clientHistory: { basis: string; accepted: number; rejected: number; noResponse: number; total: number; topRejectionReason: string | null };
  notes: string[];
}

export interface MessageDraft {
  template: string;
  language: string;
  subject: string;
  body: string;
  missing: string[];
  signaturePlaceholder: boolean;
}

/** Kandydat z silnika podpowiedzi — pola, które pokazuje ekran dostępności. */
export interface AvailabilityCandidate {
  carId: number;
  registrationNumber: string | null;
  makeAndModel: string | null;
  driverName: string | null;
  ftlCaretaker: string | null;
  accessDistance: { roadKm: number };
  accessRoute?: { km: number | null; source?: string } | null;
  /** Skąd pozycja i od kiedy wolny (kontrakt silnika — klucze danych). */
  position?: { freeFrom: string; zrodlo: string; fromOrder: string | null } | null;
  spareHours: number;
  onTime: { willMakeIt: boolean | null; reason: string | null; certain: boolean };
  delivery?: { willMakeIt: boolean | null; reason: string | null; arrivalWithReliefsAt?: string | null; loadingWindow?: { late?: boolean; arrivalWithReliefsAt?: string | null } } | null;
  accessCost?: { totalPln: number | null } | null;
  margin?: { totalCostPln: number | null } | null;
  /** Warianty tego auta przy innych zleceniach — konkurencja o pojazd. */
  candidateFor?: Array<{ externalId: string | null; userOfferId: number }>;
  busyWith?: { externalId: string | null; until: string | null } | null;
  ostrzezenia?: string[];
}

export interface Availability {
  refused: string | null;
  result: {
    candidates: AvailabilityCandidate[];
    skipped: Array<{ carId: number; reason: string; reasonText: string }>;
    skippedSummary?: Record<string, number>;
    skippedLabels?: Record<string, string>;
    order?: { route: unknown };
  } | null;
  indicatedElsewhere: Record<number, string[]>;
}

export async function fetchInquiries(filter: InquiryFilter = {}): Promise<InquiryRow[]> {
  const { data } = await axiosNest.get<InquiryRow[]>('/inquiries', { params: filter });
  return data ?? [];
}

export async function fetchInquiry(id: number | string): Promise<InquiryDetail> {
  const { data } = await axiosNest.get<InquiryDetail>(`/inquiries/${id}`);
  return data;
}

export async function createInquiry(input: PreOrderInput & { sourceNote?: string | null }): Promise<InquiryDetail> {
  const { data } = await axiosNest.post<InquiryDetail>('/inquiries', {
    ...toApiInput(input),
    sourceNote: input.sourceNote ?? null,
  });
  return data;
}

export async function updateInquiry(
  id: number | string,
  input: PreOrderInput & { sourceNote?: string | null },
): Promise<InquiryDetail> {
  const { data } = await axiosNest.patch<InquiryDetail>(`/inquiries/${id}`, {
    ...toApiInput(input),
    sourceNote: input.sourceNote ?? null,
  });
  return data;
}

export async function fetchAvailability(id: number | string): Promise<Availability> {
  const { data } = await axiosNest.get<Availability>(`/inquiries/${id}/availability`);
  return data;
}

const post = async (url: string, body?: unknown): Promise<InquiryDetail> => {
  const { data } = await axiosNest.post<InquiryDetail>(url, body ?? {});
  return data;
};

export const indicateCar = (id: number, carId: number, note?: string) =>
  post(`/inquiries/${id}/indicate`, { carId, note });
export async function clearIndication(id: number): Promise<InquiryDetail> {
  const { data } = await axiosNest.delete<InquiryDetail>(`/inquiries/${id}/indicate`);
  return data;
}
export const forwardToFtl = (id: number, note?: string) => post(`/inquiries/${id}/forward-to-ftl`, { note });
export const ftlRespond = (id: number, response: 'possible' | 'not_possible', note?: string) =>
  post(`/inquiries/${id}/ftl-response`, { response, note });
export const tradeSignal = (id: number, note?: string) => post(`/inquiries/${id}/trade-signal`, { note });
export const declineInquiry = (id: number, reason: string) => post(`/inquiries/${id}/decline`, { reason });
export const locateInquiry = (id: number) => post(`/inquiries/${id}/locate`);

export async function fetchConfirmationDraft(id: number, language: string): Promise<MessageDraft> {
  const { data } = await axiosNest.get<MessageDraft>(`/inquiries/${id}/confirmation-draft`, { params: { language } });
  return data;
}
export const confirmationSent = (id: number, subject: string, body: string) =>
  post(`/inquiries/${id}/confirmation-sent`, { subject, body });

export async function fetchQuoteBasis(id: number, price?: number | null, currency?: string): Promise<QuoteBasis> {
  const { data } = await axiosNest.get<QuoteBasis>(`/inquiries/${id}/quote-basis`, {
    params: { ...(price ? { price } : {}), ...(currency ? { currency } : {}) },
  });
  return data;
}
export const proposeQuote = (id: number, price: number, currency: string, note?: string) =>
  post(`/inquiries/${id}/quotes`, { price, currency, note });
export const approveQuote = (id: number, quoteId: number, body: { price?: number; note?: string }) =>
  post(`/inquiries/${id}/quotes/${quoteId}/approve`, body);
export async function fetchOfferDraft(id: number, quoteId: number, language: string): Promise<MessageDraft> {
  const { data } = await axiosNest.get<MessageDraft>(`/inquiries/${id}/quotes/${quoteId}/offer-draft`, {
    params: { language },
  });
  return data;
}
export const offerSent = (id: number, quoteId: number, subject: string, body: string, language: string) =>
  post(`/inquiries/${id}/quotes/${quoteId}/offer-sent`, { subject, body, language });
export const recordOutcome = (id: number, result: 'accepted' | 'rejected' | 'no_response', reason?: string) =>
  post(`/inquiries/${id}/outcome`, { result, reason });

export interface QuoteHistory {
  counters: { accepted: number; rejected: number; noResponse: number; topRejectionReason: string | null };
  items: Array<{
    id: number;
    inquiryId: number;
    ref: string;
    clientName: string | null;
    relation: string;
    proposedAt: string;
    price: number | null;
    currency: string;
    approved: boolean;
    offerSent: boolean;
    superseded: boolean;
    result: string | null;
    resultLabel: string | null;
    resultReason: string | null;
  }>;
}

export async function fetchQuoteHistory(): Promise<QuoteHistory> {
  const { data } = await axiosNest.get<QuoteHistory>('/inquiries/history');
  return data;
}

/** `mailto:` bez adresu — adresata wpisuje człowiek w swojej poczcie. */
export function mailtoHref(subject: string, body: string): string {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/** „1 590 EUR”, bez groszy, gdy ich nie ma. */
export function moneyText(amount: number | null | undefined, currency: string | null | undefined): string {
  if (amount === null || amount === undefined) return '—';
  return `${amount.toLocaleString('pl-PL', { maximumFractionDigits: 2 })} ${currency ?? ''}`.trim();
}

export { toErrorMessage };
